import { Field, Panel } from '@/components/admin/Field'
import { BUILTIN_ADMIN } from '@/lib/adminAuth'
import { lumi } from '@/lib/lumi'
import type { AuthorAccount } from '@/types/author'
import {Trash2} from 'lucide-react'
import { FormEvent, useEffect, useState } from 'react'
import toast from 'react-hot-toast'

function nowIso() {
  return new Date().toISOString()
}

function AuthorsManager() {
  const empty: Partial<AuthorAccount> = {
    fullName: '',
    username: '',
    password: '',
    active: true,
  }
  const [authors, setAuthors] = useState<AuthorAccount[]>([])
  const [form, setForm] = useState<Partial<AuthorAccount>>(empty)
  const [editingId, setEditingId] = useState('')
  const [loading, setLoading] = useState(true)

  const load = async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.authors.list({
        sort: { createdAt: -1 },
        limit: 100,
      })
      setAuthors((list || []) as AuthorAccount[])
    } catch (error) {
      console.error(error)
      toast.error('Não foi possível carregar os autores.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const save = async (event: FormEvent) => {
    event.preventDefault()
    const fullName = (form.fullName || '').trim()
    const username = (form.username || '').trim()
    const password = form.password || ''
    if (!fullName || !username || (!editingId && !password)) {
      toast.error('Preencha nome completo, nome de utilizador e senha.')
      return
    }
    if (username.toLowerCase() === BUILTIN_ADMIN.username.toLowerCase()) {
      toast.error('Esse nome de utilizador está reservado ao administrador.')
      return
    }
    const duplicated = authors.some((item) => {
      if (item._id && item._id === editingId) return false
      return (item.username || '').trim().toLowerCase() === username.toLowerCase()
    })
    if (duplicated) {
      toast.error('Já existe um autor com esse nome de utilizador.')
      return
    }
    try {
      const payload: Record<string, unknown> = {
        fullName,
        username,
        active: form.active !== false,
        updatedAt: nowIso(),
      }
      if (password) payload.password = password
      if (editingId) {
        await lumi.entities.authors.update(editingId, payload)
      } else {
        await lumi.entities.authors.create({
          ...payload,
          password,
          creator: 'admin',
          createdAt: nowIso(),
        })
      }
      toast.success('Autor guardado.')
      setForm(empty)
      setEditingId('')
      await load()
    } catch (error) {
      console.error(error)
      toast.error('Não foi possível guardar o autor.')
    }
  }

  const remove = async (id?: string) => {
    if (!id) return
    try {
      await lumi.entities.authors.delete(id)
      toast.success('Autor removido.')
      await load()
    } catch (error) {
      console.error(error)
      toast.error('Não foi possível remover o autor.')
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Panel title={editingId ? 'Editar autor' : 'Cadastrar autor'}>
        <form onSubmit={save} className="grid gap-3">
          <Field
            label="Nome completo"
            value={form.fullName || ''}
            onChange={(e) => setForm({ ...form, fullName: e.target.value })}
            required
          />
          <Field
            label="Nome de utilizador"
            value={form.username || ''}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            required
          />
          <p className="-mt-1 text-xs text-slate-500">O nome de utilizador aparece como Autor nas matérias.</p>
          <Field
            label={editingId ? 'Senha (deixe em branco para manter)' : 'Senha'}
            type="password"
            value={form.password || ''}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required={!editingId}
            autoComplete="new-password"
          />
          <label className="inline-flex items-center gap-2 text-sm font-semibold">
            <input
              type="checkbox"
              checked={form.active !== false}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            Conta ativa
          </label>
          <button className="rounded-full bg-[#0b2ea8] py-3 font-bold text-white">
            {editingId ? 'Guardar alterações' : 'Cadastrar autor'}
          </button>
        </form>
      </Panel>
      <Panel title="Autores cadastrados">
        {loading ? <p className="text-sm text-slate-500">A carregar autores...</p> : null}
        <div className="space-y-3">
          {authors.map((item) => (
            <article key={item._id} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
              <div className="min-w-0 flex-1">
                <p className="font-bold">{item.fullName}</p>
                <p className="text-sm text-slate-500">@{item.username} · {item.active === false ? 'Inativo' : 'Ativo'}</p>
              </div>
              <button
                type="button"
                className="text-sm font-bold text-[#0b2ea8]"
                onClick={() => {
                  setForm({ ...item, password: '' })
                  setEditingId(item._id || '')
                }}
              >
                Editar
              </button>
              <button type="button" onClick={() => void remove(item._id)} className="text-red-500">
                <Trash2 size={16} />
              </button>
            </article>
          ))}
          {!loading && !authors.length ? <p className="text-sm text-slate-500">Ainda não há autores.</p> : null}
        </div>
      </Panel>
    </div>
  )
}

export default AuthorsManager
